import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import { FieldError } from '@/ui';
import NativeSelectField from '../FormFields/NativeSelectField';
import { TabPane } from './TabPane';
import { DomainSortButton } from './components/DomainSortButton';
import { change, Field, FieldArray } from 'redux-form';
import { getQuestionAnswers } from '../../actions';
import { useAffiliateTags } from '../../views/Affiliates/queries';
import { useAffiliates, useDomains, useUserAttributes } from '../../views/OfferBlocks/queries';
import {
  renderComboBoxSecond,
  renderCriteriaOptions,
  renderField,
  renderMultiSelect,
  textarea,
} from '../FormFields/reduxFormRenderers';
import { useOfferQuestions } from '../../views/Offers/queries';
import { AffiliateComboBox } from './components/AffiliateComboBox';
import CriteriaLandingPageIncludes from './CriteriaLandingPageIncludes';
import DomainAffiliate from './DomainAffiliate';
import { useAttributeSelectors } from './hooks';
import { UserAttribute } from './UserAttribute.tsx';
import { sequenceDetailsSelector } from '../../views/Sequences/actions/reducers';
import { getFormSelector } from '../../reducers/FormSelectorReducer';

// Custom render component for andOrLogic select (like PreviousRegistration)
const renderAndOrSelect = ({ input, meta: { error, touched } }) => {
  // Explicitly handle null/undefined values to ensure empty string
  const value = input.value === null || input.value === undefined || input.value === '' ? '' : input.value;
  return (
    <div>
      <NativeSelectField
        value={value}
        onChange={input.onChange}
        onBlur={input.onBlur}
        onFocus={input.onFocus}
        error={Boolean(error && touched)}
      >
        <option value=''>Select Logic</option>
        <option value='AND'>All entries must pass</option>
        <option value='OR'>Any of these entries can pass</option>
        <option value='ANY'>Each entry evaluated independently</option>
      </NativeSelectField>
      <FieldError error={error} touched={touched} />
    </div>
  );
};

// Render rows for Aff SubID Configuration (defined OUTSIDE component like PreviousRegistration)
const renderAffSubIdRows = ({ fields, andOrLogicFieldVal, affiliateNames, isLoading }) => {
  useEffect(() => {
    if (fields.length === 0) {
      fields.push({
        includeExclude: null,
        affiliate: null,
        subIds: null,
      });
    }
  }, [fields]);

  if (!['AND', 'OR', 'ANY'].includes(andOrLogicFieldVal)) {
    return null;
  }

  return (
    <div>
      {fields.map((fieldName, index) => (
        <Box key={index} sx={{ mb: 3 }}>
          <Grid container spacing={2} sx={{ alignItems: 'start' }}>
            {fields.length > 1 && (
              <Grid size={1}>
                <Tooltip title='Remove Aff SubID Configuration Row'>
                  <IconButton
                    type='button'
                    color='error'
                    aria-label='Remove Aff SubID Configuration Row'
                    onClick={() => fields.remove(index)}
                  >
                    <Close fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}

            <Grid size={2}>
              <Field name={`${fieldName}.includeExclude`} component={renderCriteriaOptions} type='select' />
            </Grid>

            <Grid size={4}>
              <Field
                name={`${fieldName}.affiliate`}
                component={AffiliateComboBox}
                affiliates={affiliateNames}
                busy={isLoading}
              />
            </Grid>

            <Grid size={4}>
              {/* `data`/`allowCreate` used to sit here, left over from a react-widgets
                  renderer. They did nothing, and now that `textarea` forwards its extra
                  props they would reach the DOM and warn. */}
              <Field name={`${fieldName}.subIds`} component={textarea} />
            </Grid>
          </Grid>

          {index === fields.length - 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Tooltip title='Add Aff SubID Configuration Row'>
                <IconButton
                  type='button'
                  color='success'
                  aria-label='Add Aff SubID Configuration Row'
                  onClick={() =>
                    fields.push({
                      includeExclude: null,
                      affiliate: null,
                      subIds: null,
                    })
                  }
                >
                  <Add />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      ))}
    </div>
  );
};

export const AttributePane = ({ criteriaType, isActiveTab }) => {
  const dispatch = useDispatch();
  const [shouldQuestionUpdate, setShouldQuestionUpdate] = useState(true);
  const {
    form,
    selectedQuestion,
    criteriaIncludeOrExcludeAffNames: criteriaAffiliate,
    criteriaIncludeOrExcludeEmailDomains: criteriaEmail,
    userAttributeSelect,
    affSubIDConfiguration,
  } = useAttributeSelectors();
  const { data: userAttributes } = useUserAttributes(true, (data) => _.orderBy(data, ['name'], ['asc']));
  const { data: affiliates = [], isLoading: affiliatesLoading } = useAffiliates(true, (data = []) =>
    _.orderBy(data, ['name'], ['asc']).map(({ name, networkAffiliateId }) => ({
      id: networkAffiliateId,
      name,
    }))
  );
  const [domainSortDirection, setDomainSortDirection] = useState('asc');
  const { data: domains, isLoading: domainsLoading } = useDomains(true, (data) =>
    data.map((domain) => ({ id: domain.id, name: domain.name })).sort((a, b) => a.id - b.id)
  );
  const { data: criteriaQuestions, isLoading: criteriaQuestionsLoading } = useOfferQuestions({
    enabled: criteriaType === 'Offer Criteria' && isActiveTab,
  });
  const [questionAnswers, setQuestionAnswers] = useState([]);

  const { data: affiliateTags, isLoading: affiliateTagsLoading } = useAffiliateTags('affiliate');
  const affiliateNames =
    affiliatesLoading || affiliateTagsLoading
      ? [{ name: '[None]', id: null }]
      : [
          { name: '[Null]', id: '[Null]' },
          ...(affiliateTags?.map((tag) => ({ id: `#${tag.name}`, name: `#${tag.name}` })) || []),
          ...affiliates,
        ];
  const { data: domainTags, isLoading: domainTagsLoading } = useAffiliateTags('domain');
  const domainsWithTags =
    domainsLoading || domainTagsLoading
      ? []
      : [
          ...(domainTags?.map((tag) => ({ id: `#${tag.name}`, name: `#${tag.name}` })) || []),
          ...(domainSortDirection === 'desc' ? [...domains].reverse() : domains),
        ];

  // Get andOrLogic value from Redux-form state (like PreviousRegistration does)
  const andOrLogicFieldVal = useSelector((state) => {
    const formSelector = state?.form?.sequenceDetails ? sequenceDetailsSelector : getFormSelector();
    if (!formSelector) {
      console.warn('Form selector not found in state');
      return null;
    }
    return formSelector(state, 'affSubIDConfiguration.andOrLogic');
  });

  useEffect(() => {
    if (shouldQuestionUpdate && criteriaQuestions?.length > 0) {
      const criteriaQuestionObj = _.find(criteriaQuestions, ({ question }) => question === selectedQuestion);
      if (criteriaQuestionObj) {
        dispatch(getQuestionAnswers(criteriaQuestionObj.id)).then((answers) => {
          setQuestionAnswers(answers);
        });
        setShouldQuestionUpdate(false);
      }
      dispatch(change(form, 'criteriaQuestion', selectedQuestion));
    }
  }, [shouldQuestionUpdate, dispatch, criteriaQuestions, selectedQuestion, form]);

  return (
    <TabPane tabId='Attribute'>
      <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
        <Grid size='auto'>
          <Typography variant='h5'>User Attributes</Typography>
        </Grid>
        <Grid size='auto'>
          <Field name='criteriaIncludeOrExcludeUserAttributes' component={renderCriteriaOptions} type='select' />
        </Grid>
        {(userAttributeSelect === 'Include' || userAttributeSelect === 'Exclude') && (
          <Grid size='grow'>
            <Field name='userAttributeRules' component={UserAttribute} userAttributes={userAttributes} />
          </Grid>
        )}
      </Grid>
      <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
        <Grid size={{ xs: 12, lg: 2 }}>
          <Typography variant='h5'>Affiliate Names</Typography>
        </Grid>
        <Grid size={{ xs: 12, lg: 2 }}>
          <Field name='criteriaIncludeOrExcludeAffNames' component={renderCriteriaOptions} type='select' />
        </Grid>
        {(criteriaAffiliate === 'Include' || criteriaAffiliate === 'Exclude') && (
          <Grid size={{ xs: 12, lg: 4 }}>
            <Field
              name='criteriaAffNames'
              component={renderMultiSelect}
              textField={(item) => (item.name && item.name.startsWith('#') ? `${item.id}` : `${item.id}: ${item.name}`)}
              dataKey='name'
              data={affiliateNames}
              busy={affiliatesLoading || affiliateTagsLoading}
              groupBy={(item) => (item.name.startsWith('#') ? 0 : 1)}
              renderListGroup={({ group }) => (group === 0 ? <span>Tags</span> : <span>Affiliates</span>)}
            />
          </Grid>
        )}
      </Grid>
      <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
        <Grid size={{ xs: 12, lg: 2 }}>
          <Typography variant='h5'>Aff SubID Configuration</Typography>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Field
            name='affSubIDConfiguration.andOrLogic'
            component={renderAndOrSelect}
            type='select'
            normalize={(value) => {
              // Explicitly return empty string for null/undefined/empty values
              if (value === null || value === undefined || value === '') return '';
              return value;
            }}
            format={(value) => {
              // Explicitly return empty string for null/undefined/empty values
              if (value === null || value === undefined || value === '') return '';
              return value;
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 10 }} sx={{ mt: 2 }}>
          <FieldArray
            name='affSubIDConfiguration.subIdList'
            component={renderAffSubIdRows}
            props={{
              andOrLogicFieldVal,
              affiliateNames,
              isLoading: affiliatesLoading || affiliateTagsLoading,
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
        <Grid size={{ xs: 12, lg: 2 }}>
          <Stack direction='row' spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant='h5'>Domain-specific Affiliates</Typography>
            <DomainSortButton
              direction={domainSortDirection}
              onToggle={() => setDomainSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
            />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, lg: 10 }}>
          <DomainAffiliate
            domains={domainsWithTags} // Includes both domain names and tags
            affiliates={affiliateNames} // Includes both affiliate names and tags
            isLoading={domainsLoading || domainTagsLoading}
          />
        </Grid>
      </Grid>
      <CriteriaLandingPageIncludes />
      <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
        <Grid size={{ xs: 12, lg: 2 }}>
          <Typography variant='h5'>Email Domains</Typography>
        </Grid>
        <Grid size={{ xs: 12, lg: 2 }}>
          <Field name='criteriaIncludeOrExcludeEmailDomains' component={renderCriteriaOptions} type='select' />
        </Grid>
        {(criteriaEmail === 'Include' || criteriaEmail === 'Exclude') && (
          <Grid size={{ xs: 12, lg: 4 }}>
            <Field
              name='criteriaEmailDomains'
              component={renderMultiSelect}
              data={[
                'aim.com',
                'aol.com',
                'gmail.com',
                'google.com',
                'hotmail.com',
                'live.com',
                'outlook.com',
                'sbcglobal.net',
                'yahoo.com',
                'icloud.com',
                'me.com',
                'mac.com',
              ]}
            />
          </Grid>
        )}
      </Grid>
      {criteriaType === 'Offer Criteria' && (
        <>
          <Box sx={{ mt: 2 }}>
            <FormLabel component='span' sx={{ display: 'block', mb: 0.5 }}>
              Criteria Question
            </FormLabel>
            <Field
              name='criteriaQuestion'
              component={renderComboBoxSecond}
              onSelect={({ id }) =>
                dispatch(getQuestionAnswers(id)).then((answers) => {
                  setQuestionAnswers(answers);
                })
              }
              data={criteriaQuestions}
              textField={(item) => (typeof item === 'string' ? item : `${item.name} - ${item.question}`)}
              valueField='question'
              placeholder='Select Question'
              busy={criteriaQuestionsLoading}
            />
          </Box>
          {selectedQuestion && (
            <Box sx={{ mt: 2 }}>
              <FormLabel component='span' sx={{ display: 'block', mb: 0.5 }}>
                Criteria Question Answers
              </FormLabel>
              <Field
                name='criteriaQuestionAnswers'
                component={renderMultiSelect}
                type='select'
                data={questionAnswers}
              />
            </Box>
          )}
        </>
      )}
    </TabPane>
  );
};
